import React, {useEffect, useState} from 'react'
import Chart from "react-apexcharts";
import {FutureCalc} from "../../../../components/calculator/Calculator";
import {numberToMoneyFormat} from "../../../../stores/StoreUtilities";
import {globalColors} from "../../../../styles/globalStyles"
import {Paper, TableContainer, useTheme} from "@mui/material";
import cloneDeep from "lodash.clonedeep";

const defaultOptions = {
  chart: {
    id: "budget-projection",
    type: 'bar',
    stacked: true,
    height: 350,
    width: "100%",
    // zoom: {
    //   autoScaleYaxis: true
    // },
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    zoom: {
      enabled: false,
      autoScaleYaxis: true
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  title: {
    text: "",
    align: "right",
    margin: 0,
    floating: true,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left',
    onItemClick: {
      toggleDataSeries: false
    }
  },
  xaxis: {
    type: 'number',
    min: 2000,
    axisBorder: {
      show: true
    }
  },
  yaxis: {
    labels: {
      formatter: (val: number) => {
        return numberToMoneyFormat(val, 0)
        // if (val < 1000000) {
        //   return `$${val / 1000}K`
        // } else {
        //   return `$${val / 1000000}M`
        // }
      }
    }
  },
  tooltip: {
    y: {
      formatter: function (value: number) {
        return numberToMoneyFormat(value, 0)
      }
    }
  },
  // tooltip: {
  //   x: {
  //     format: 'dd MMM yyyy'
  //   }
  // },
  colors: [globalColors.income, globalColors.withdrawal, globalColors.deductionExpense, globalColors.taxExpense, globalColors.essentialExpense, globalColors.discretionaryExpense],
  // fill: {
  //   type: 'gradient',
  //   gradient: {
  //     shadeIntensity: 1,
  //     opacityFrom: 0.7,
  //     opacityTo: 0.9,
  //     stops: [0, 100]
  //   }
  // },
  // stroke: {
  //   width: 3
  // },
  resonsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const BudgetProjectionChart = ({
  year,
  futureCalc,
  onChange,
  height,
  width,
  title
}: {
  year: number
  futureCalc: FutureCalc
  onChange?: (year: number) => any
  height?: string
  width?: string
  title?: string
}) => {
  const [startYear, setStartYear] = useState<number>(0)
  const [endYear, setEndYear] = useState<number>(0)
  const [calc, setCalc] = useState<FutureCalc | undefined>()
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [currentYear, setCurrentYear] = useState<number>(0)

  const theme = useTheme()

  useEffect(() => {
    if (futureCalc.futureYears.length > 0) {
      const start = futureCalc.futureYears[0].year
      setStartYear(start)
      setEndYear(futureCalc.futureYears[futureCalc.futureYears.length-1].year)
      setCurrentYear(start)
      setCalc(futureCalc)
    }
  }, [year, futureCalc])

  useEffect(() => {
    if (startYear > 0 && calc && calc.futureYears.length > 0) {
      calculateData(startYear)
    }
  }, [currentYear, calc])

  const calculateData = (startYear: number) => {
    if (!calc || startYear <= 0 || endYear < startYear) {
      return
    }
    let incomeData = []
    let deductionData = []
    let taxData = []
    let essentialData = []
    let discretionaryData = []
    let withdrawalData = []
    for (let index = 0; index < calc.futureYears.length; index++) {
      const year = startYear + index
      const fy = calc.futureYears[index]
      if (fy.surplus > 0) {
        incomeData.push({x: year, y: fy.grossIncome})
        withdrawalData.push({x: year, y: fy.rmds})
      } else {
        incomeData.push({x: year, y: fy.grossIncome})
        withdrawalData.push({x: year, y: -fy.surplus + fy.rmds})
      }
      deductionData.push({x: year, y: fy.deductions})
      taxData.push({x: year, y: fy.taxes})
      essentialData.push({x: year, y: fy.essentialExpenses})
      discretionaryData.push({x: year, y: fy.discretionaryExpenses})
    }

    const series = [
      {
        name: "Income",
        group: "Income",
        data: incomeData
      },
      {
        name: "Withdrawals",
        group: "Income",
        data: withdrawalData
      },
      {
        name: "Deductions",
        group: "Expenses",
        data: deductionData
      },
      {
        name: "Taxes",
        group: "Expenses",
        data: taxData
      },
      {
        name: "Essential",
        group: "Expenses",
        data: essentialData
      },
      {
        name: "Discretionary",
        group: "Expenses",
        data: discretionaryData
      },
    ]
    setSeries(series)

    const newOptions: any = cloneDeep(defaultOptions)
    newOptions.title.text = title ?? ""
    newOptions.xaxis.min = startYear

    if (currentYear !== undefined) {
      const annotations = {
        xaxis: [
          {
            x: currentYear,
            borderColor: theme.palette.secondary.main, //'#00E396',
            label: {
              borderColor: theme.palette.secondary.main, //'#00E396',
              orientation: 'horizontal',
              text: currentYear.toString(),
              style: {
                fontSize: '14px',
                fontWeight: 600
              }
            }
          }
        ]
      }
      newOptions["annotations"] = annotations
      newOptions.chart.events["dataPointSelection"] = (event: any, chartContext: any, details: {dataPointIndex: number, seriesIndex: number}) => {
        if (details.dataPointIndex >= 0 && onChange) {
          onChange(details.dataPointIndex + startYear)
        }
      }
    }

    if (height) {
      newOptions.chart.toolbar.show = false
    }

    setOptions(newOptions)
  }

  // const handleChange = (year: number) => {
  //   setCurrentYear(year)
  //   if (onChange) {
  //     onChange(year)
  //   }
  // }

  if (options && series) {
    return (
        <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden"}}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height={height ?? "500"}
            width={width ?? "100%"}
          />
        </TableContainer>
    )
  } else {
    return null
  }
}

export default BudgetProjectionChart